import { routing } from '@/i18n/routing';

export const REDIRECT_QUERY_PARAM = 'next';
export const REDIRECT_FALLBACK = '/dashboard';
const STORAGE_KEY = 'safha:post-login-redirect';
const EXPLICIT_LOGOUT_KEY = 'safha:explicit-logout';

const FORBIDDEN_PREFIXES = [
  '/api',
  '/login',
  '/forgot-password',
  '/reset-password'
];

/**
 * Validates and normalizes a candidate redirect path. Returns the
 * locale-stripped form when safe; otherwise returns null.
 *
 * Same-origin relative paths only. Rejects:
 *   - empty / whitespace
 *   - missing leading "/"
 *   - protocol-relative ("//...")
 *   - absolute URLs (containing "://")
 *   - /api, /login, /forgot-password, /reset-password (auth-loop / API)
 *
 * Strips a leading locale segment (e.g. "/en/dashboard/x" -> "/dashboard/x")
 * because @/i18n/routing's router prepends the current locale automatically.
 */
export function sanitizeRedirectPath(
  raw: string | null | undefined
): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null;
  if (trimmed.includes('://')) return null;

  let path = trimmed;
  for (const locale of routing.locales) {
    if (path === `/${locale}`) {
      path = '/';
      break;
    }
    if (path.startsWith(`/${locale}/`)) {
      path = path.slice(locale.length + 1);
      break;
    }
  }

  for (const prefix of FORBIDDEN_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return null;
  }

  return path;
}

function safeSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Marks the next unauthenticated render as a deliberate sign-out, so the page
 * being left behind is not remembered as a post-login destination.
 *
 * Without it `logout()` cleared the stored path and `AuthGuard` immediately
 * put it back: flipping `status` to `unauthenticated` runs the guard's capture
 * effect on the page the user just left. The next account then logged in
 * straight onto that page and met a full-page "Access Denied" (OMA-605).
 */
export function markExplicitLogout(): void {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.setItem(EXPLICIT_LOGOUT_KEY, '1');
  } catch {
    /* ignore quota / private-mode failures */
  }
}

/** Reads and clears the sign-out marker — single use, like the redirect itself. */
function consumeExplicitLogout(): boolean {
  const store = safeSessionStorage();
  if (!store) return false;
  try {
    const marked = store.getItem(EXPLICIT_LOGOUT_KEY) === '1';
    store.removeItem(EXPLICIT_LOGOUT_KEY);
    return marked;
  } catch {
    return false;
  }
}

/**
 * Reads window.location.pathname + window.location.search, sanitizes,
 * stores in sessionStorage. Returns the sanitized value or null.
 *
 * A no-op right after a deliberate sign-out — see `markExplicitLogout`.
 */
export function capturePendingRedirect(): string | null {
  if (typeof window === 'undefined') return null;
  if (consumeExplicitLogout()) return null;
  const candidate = window.location.pathname + window.location.search;
  const sanitized = sanitizeRedirectPath(candidate);
  if (!sanitized) return null;
  const store = safeSessionStorage();
  if (store) {
    try {
      store.setItem(STORAGE_KEY, sanitized);
    } catch {
      /* ignore quota / private-mode failures */
    }
  }
  return sanitized;
}

/**
 * Resolves the post-login destination and clears the stored value
 * (single-use). Prefers `?next=` over sessionStorage; if neither yields a
 * safe path, returns `fallback` (defaults to REDIRECT_FALLBACK).
 *
 * The structural parameter type accepts both URLSearchParams and Next.js's
 * ReadonlyURLSearchParams returned by useSearchParams().
 */
export function consumePendingRedirect(
  searchParams: { get(name: string): string | null } | null,
  fallback: string = REDIRECT_FALLBACK
): string {
  const fromQuery = sanitizeRedirectPath(
    searchParams?.get(REDIRECT_QUERY_PARAM) ?? null
  );

  const store = safeSessionStorage();
  let fromStorage: string | null = null;
  if (store) {
    try {
      fromStorage = sanitizeRedirectPath(store.getItem(STORAGE_KEY));
      store.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  return fromQuery ?? fromStorage ?? fallback;
}

/** Hard-clears any pending redirect (used by explicit logout). */
export function clearPendingRedirect(): void {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
    store.removeItem(EXPLICIT_LOGOUT_KEY);
  } catch {
    /* ignore */
  }
}
